/**
 * Carrusel de **fotos de ejemplo** de la pantalla de carga y la regla de cuándo se
 * muestra (ADR-2026-08-04-fotos-en-las-primeras-generaciones).
 *
 * Vivía en `components/onboarding/accountSetup.ts` como `SETUP_CAROUSEL_IMAGES`, cuando
 * era exclusivo del alta. Ahora también lo usa la pantalla de carga de la app en las
 * primeras generaciones, así que se mudó acá: el loader compartido no debería depender
 * de la carpeta del onboarding (mismo criterio que `SparklesLoader`,
 * ADR-2026-08-02-pantalla-carga-unificada).
 */

/**
 * Fotos del desfile "Esto es lo que podés crear con Atlas" (6, en loop continuo).
 * Sin `3.jpg` (el short de la selección): salió del desfile
 * (ADR-2026-10-18-el-short-de-la-seleccion-sale-del-carrusel-de-carga). El archivo
 * queda porque lo usa la carpeta de referencias de Recrear video.
 */
export const LOADING_CAROUSEL_IMAGES: string[] = [
  '/images/onboarding/generation/carousel/1.jpg',
  '/images/onboarding/generation/carousel/2.jpg',
  '/images/onboarding/generation/carousel/4.jpg',
  '/images/onboarding/generation/carousel/5.jpg',
  '/images/onboarding/generation/carousel/6.jpg',
  '/images/onboarding/generation/carousel/7.jpg',
];

/**
 * Cuántas generaciones ven las **fotos** antes de pasar a los consejos. La primera es
 * la del alta: quien recién llega todavía no sabe qué puede lograr con Atlas y las fotos
 * se lo muestran; para la cuarta ya lo sabe y la espera rinde más con un consejo.
 */
export const GENERACIONES_CON_FOTOS = 3;

/**
 * Cuántas veces vio el usuario la pantalla de carga. Es **por dispositivo**: no hay
 * historial de generaciones en el cliente y consultarlo a Supabase pediría tres
 * consultas encadenadas justo al montar la pantalla, con el marquee saltando de fotos a
 * consejos a mitad de camino cuando llega la respuesta.
 *
 * Consecuencias asumidas: quien usa la app en el celular y en la compu ve las fotos
 * hasta 3 veces **en cada uno**, y borrar los datos del navegador reinicia la cuenta.
 * Para una decisión puramente cosmética es un precio barato. Para reiniciarlo a mano:
 * `localStorage.removeItem('atlas:generaciones_vistas')`.
 */
const KEY = 'atlas:generaciones_vistas';

function leerCuenta(): number {
  try {
    const n = Number(localStorage.getItem(KEY));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    // Modo privado / storage bloqueado: se comporta como un usuario nuevo (ve fotos).
    return 0;
  }
}

/**
 * ¿Esta generación va con fotos? **Lectura pura**, sin efectos: se puede llamar durante
 * el render y en el inicializador de un `useState` sin que StrictMode la descuadre.
 */
export function generacionMuestraFotos(): boolean {
  return leerCuenta() < GENERACIONES_CON_FOTOS;
}

/**
 * Suma una generación. Va en un efecto y **una sola vez por pantalla de carga**; el
 * llamador tiene que protegerlo con un ref, porque en StrictMode los efectos de montaje
 * corren dos veces y contaría de a dos.
 *
 * Deja de escribir al llegar al tope: no hay razón para que el número crezca para
 * siempre, y así el valor guardado se lee de un vistazo.
 */
export function registrarGeneracion(): void {
  try {
    const actual = leerCuenta();
    if (actual >= GENERACIONES_CON_FOTOS) return;
    localStorage.setItem(KEY, String(actual + 1));
  } catch {
    /* sin storage no se cuenta: el usuario ve fotos siempre, que es el lado amable */
  }
}
